import { useCallback, useEffect, useState } from 'react';
import { Listing, Rarity, User } from '../types';
import { marketplace as marketplaceApi } from '../api';
import SlabCard from '../components/SlabCard';
import RarityBadge from '../components/RarityBadge';

type SortOption = 'newest' | 'price_asc' | 'price_desc';

const RARITIES: { value: Rarity | ''; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'common', label: 'Common' },
  { value: 'uncommon', label: 'Uncommon' },
  { value: 'rare', label: 'Rare' },
  { value: 'ultra-rare', label: 'Ultra Rare' },
];

const SORTS: { value: SortOption; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'price_asc', label: 'Price ↑' },
  { value: 'price_desc', label: 'Price ↓' },
];

interface MarketplaceProps {
  user: User;
}

interface DetailModalProps {
  listing: Listing;
  currentUser: User;
  onClose: () => void;
  onBought: () => void;
}

function DetailModal({ listing, currentUser, onClose, onBought }: DetailModalProps) {
  const [offerPrice, setOfferPrice] = useState('');
  const [step, setStep] = useState<'view' | 'buy' | 'offer'>('view');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const tgUser = (window as Window & { Telegram?: { WebApp?: { initDataUnsafe?: { user?: { id?: number; username?: string; first_name?: string } } } } }).Telegram?.WebApp?.initDataUnsafe?.user;
  const tgId = String(tgUser?.id ?? currentUser.tg_id ?? '0');
  const tgName = tgUser?.first_name ?? currentUser.tg_name ?? 'User';

  const isMine = listing.seller_id === currentUser.id;

  async function handleBuy() {
    setLoading(true);
    setError(null);
    try {
      await marketplaceApi.buy(listing.id, tgId, tgName);
      setSuccess('Purchase complete! Card added to your collection.');
      setTimeout(() => { onBought(); onClose(); }, 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Purchase failed');
    } finally {
      setLoading(false);
    }
  }

  async function handleOffer() {
    const price = parseFloat(offerPrice);
    if (!price || price <= 0) { setError('Enter a valid price'); return; }
    setLoading(true);
    setError(null);
    try {
      await marketplaceApi.makeOffer(listing.id, price);
      setSuccess('Offer sent!');
      setTimeout(() => { onClose(); }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send offer');
    } finally {
      setLoading(false);
    }
  }

  async function handleCancel() {
    setLoading(true);
    setError(null);
    try {
      await marketplaceApi.cancel(listing.id);
      onBought();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end" onClick={onClose}>
      <div
        className="w-full bg-surface-card rounded-t-2xl p-4 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle */}
        <div className="w-10 h-1 bg-white/20 rounded-full mx-auto" />

        {/* Card preview */}
        <div className="w-36 mx-auto">
          <SlabCard card={listing} size="md" />
        </div>

        {/* Info */}
        <div className="text-center space-y-1">
          <h3 className="text-white font-bold text-lg">{listing.pokemon_name}</h3>
          <div className="flex items-center justify-center gap-2">
            <RarityBadge rarity={listing.rarity} />
            <span className="text-gray-400 text-sm">{listing.set_name}</span>
          </div>
          <p className="text-gray-400 text-sm">
            Market ref: <span className="text-white">${listing.market_price_usd.toFixed(2)}</span>
          </p>
          <p className="text-gray-400 text-sm">
            Seller: <span className="text-accent-purple-light">
              {listing.seller_username ? `@${listing.seller_username}` : listing.seller_name}
            </span>
          </p>
        </div>

        {/* Price */}
        <div className="bg-surface rounded-xl p-3 text-center">
          <p className="text-accent-gold font-black text-2xl">{listing.price_ton} TON</p>
        </div>

        {success && (
          <p className="text-green-400 text-sm text-center">{success}</p>
        )}
        {error && (
          <p className="text-red-400 text-sm text-center">{error}</p>
        )}

        {/* Actions */}
        {isMine ? (
          <button
            onClick={handleCancel}
            disabled={loading}
            className="w-full py-3 rounded-xl bg-red-900/40 text-red-400 font-semibold border border-red-500/30"
          >
            {loading ? 'Cancelling…' : 'Cancel Listing'}
          </button>
        ) : step === 'view' ? (
          <div className="space-y-2">
            <button
              onClick={() => setStep('buy')}
              className="w-full py-3 rounded-xl bg-accent-purple text-white font-bold text-base"
            >
              Buy for {listing.price_ton} TON
            </button>
            <button
              onClick={() => setStep('offer')}
              className="w-full py-3 rounded-xl bg-surface text-gray-300 font-semibold border border-white/10"
            >
              Make Offer
            </button>
          </div>
        ) : step === 'buy' ? (
          <div className="space-y-3">
            <p className="text-gray-400 text-sm text-center">
              Confirm purchase of <span className="text-white font-semibold">{listing.price_ton} TON</span>
            </p>
            <button
              onClick={handleBuy}
              disabled={loading}
              className="w-full py-3 rounded-xl bg-accent-purple text-white font-bold"
            >
              {loading ? 'Processing…' : 'Confirm Buy'}
            </button>
            <button onClick={() => setStep('view')} className="w-full py-2 text-gray-400 text-sm">
              Back
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <input
              type="number"
              placeholder="Your offer in TON"
              value={offerPrice}
              onChange={(e) => setOfferPrice(e.target.value)}
              className="w-full bg-surface text-white rounded-xl px-4 py-3 outline-none border border-white/10 focus:border-accent-purple"
            />
            <button
              onClick={handleOffer}
              disabled={loading}
              className="w-full py-3 rounded-xl bg-accent-purple text-white font-bold"
            >
              {loading ? 'Sending…' : 'Send Offer'}
            </button>
            <button onClick={() => setStep('view')} className="w-full py-2 text-gray-400 text-sm">
              Back
            </button>
          </div>
        )}

        <button onClick={onClose} className="w-full py-2 text-gray-500 text-sm">
          Close
        </button>
      </div>
    </div>
  );
}

export default function Marketplace({ user }: MarketplaceProps) {
  const [listings, setListings] = useState<Listing[]>([]);
  const [rarity, setRarity] = useState<Rarity | ''>('');
  const [sort, setSort] = useState<SortOption>('newest');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Listing | null>(null);

  const loadListings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await marketplaceApi.list({
        rarity: rarity || undefined,
        sort,
        search: search || undefined,
        limit: 40,
      });
      setListings(res.listings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  }, [rarity, sort, search]);

  useEffect(() => { loadListings(); }, [loadListings]);

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSearch(searchInput);
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-4 pt-3 pb-2 border-b border-white/5 space-y-2">
        <div className="flex items-center justify-between">
          <h2 className="text-white font-bold text-lg">Marketplace</h2>
          <span className="text-gray-500 text-sm">{listings.length} listings</span>
        </div>

        {/* Search */}
        <form onSubmit={handleSearchSubmit} className="flex gap-2">
          <input
            type="text"
            placeholder="Search cards…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="flex-1 bg-surface text-white text-sm rounded-xl px-3 py-2 outline-none border border-white/10 focus:border-accent-purple placeholder-gray-500"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => { setSearchInput(''); setSearch(''); }}
              className="text-gray-400 px-2"
            >
              ✕
            </button>
          )}
        </form>

        {/* Rarity filters */}
        <div className="flex gap-1.5 overflow-x-auto pb-0.5 scrollbar-none">
          {RARITIES.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setRarity(value)}
              className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                rarity === value
                  ? 'bg-accent-purple text-white'
                  : 'bg-surface-card text-gray-400 border border-white/5'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Sort */}
        <div className="flex gap-1 bg-surface rounded-xl p-1">
          {SORTS.map(({ value, label }) => (
            <button
              key={value}
              onClick={() => setSort(value)}
              className={`flex-1 py-1 text-xs rounded-lg font-medium transition-colors ${
                sort === value ? 'bg-accent-purple text-white' : 'text-gray-400'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 scroll-area p-3">
        {loading && (
          <div className="grid grid-cols-2 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="rounded-xl bg-surface-card animate-pulse aspect-[3/5]" />
            ))}
          </div>
        )}

        {error && (
          <div className="text-center py-10">
            <p className="text-red-400 text-sm">{error}</p>
            <button onClick={loadListings} className="text-accent-purple-light text-sm mt-2">Retry</button>
          </div>
        )}

        {!loading && !error && listings.length === 0 && (
          <div className="text-center py-16 space-y-3">
            <p className="text-5xl">🛒</p>
            <p className="text-white font-semibold">No listings found</p>
            <p className="text-gray-400 text-sm">
              {rarity || search ? 'Try different filters' : 'List a card from your collection!'}
            </p>
          </div>
        )}

        {!loading && !error && listings.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {listings.map((listing) => (
              <div key={listing.id} className="space-y-1" onClick={() => setSelected(listing)}>
                <SlabCard card={listing} size="md" onClick={() => setSelected(listing)} />
                <div className="px-1">
                  <p className="text-accent-gold font-bold text-sm">{listing.price_ton} TON</p>
                  <p className="text-gray-500 text-xs truncate">
                    {listing.seller_username ? `@${listing.seller_username}` : listing.seller_name}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <DetailModal
          listing={selected}
          currentUser={user}
          onClose={() => setSelected(null)}
          onBought={() => { setSelected(null); loadListings(); }}
        />
      )}
    </div>
  );
}
